import * as React from 'react';
import { cloneDeep } from 'lodash';
import { API, USER_CHANNEL_NAME_MAP, RESPONSE_CHANNEL_TYPE, contentFontColor } from '@config/index';
import {
  TestCase,
  TestCaseTurn,
  TestCaseRun,
  testCaseTurnToServer,
  testCaseRunToLocal,
  AgentContext,
  AgentContextProps
} from '@interface/index';
import {
  Header,
  CommonBreadcrumbItem,
  NiTable,
  NiSpin,
  AddButton,
  AddVersionObserver,
  RemoveVersionObserver,
  RefreshVersion,
  Overlay,
  HeaderComp,
  NiInput,
  BodyGroup,
  NiButton,
  Description,
  niConfirm
} from '@component/index';
import {
  Drawer,
  Progress,
  Upload,
  Space,
  Popover,
  message
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { DeleteOutlined, BugOutlined } from '@ant-design/icons';
import { RouteComponentProps } from 'react-router-dom';
import { localizedString } from '@locale/index';
import { instance, isReadMode, isEqual, getOrgId, renderTime, getAgentId, getUserId, agentBaseComp } from '@lib/index';
import '@style/component/test-case/index.less';
import { TryItNowSession } from '@component/try-it-now/session';
import { EditPermission } from '@permission/agent';

const TextArea = NiInput.TextArea;

export type TestCaseCompProps = {};
// RouteComponentProps<{}> &
// {
// };

interface TestCaseCompState {
  loading: boolean;
  data: TestCaseRun[];
  testRecord?: TestCaseRun;
  running: boolean;
  percent: number;
}

export class TestCaseComp extends React.Component<TestCaseCompProps, TestCaseCompState> {
  $bodyContent?: HTMLDivElement;
  $header?: HeaderComp;

  constructor(props: TestCaseCompProps) {
    super(props);

    this.state = {
      loading: false,
      data: [],
      testRecord: undefined,
      running: false,
      percent: 0
    };
  }


  componentDidMount() {
    AddVersionObserver(this);
    instance.on('test_case_add', () => {
      this.onInit();
    });
    this.onInit();
  }

  onVersionChanged() {
    this.onInit();
  }

  componentWillUnmount() {
    instance.off('test_case_add');
    RemoveVersionObserver(this);
  }

  render() {
    const {
      loading,
      data,
      testRecord,
      running,
      percent
    } = this.state;

    const disabled = isReadMode();

    const run = testRecord;
    const hasRun = run && run.turns && run.turns.length > 0 || false;
    const turns = run && (
      hasRun
        ? run.turns
        : run.testCase.turns
    );

    const expected = run && (
      hasRun ? run.testCase.turns : undefined
    );

    const channel = turns && this.getDifferentChannel(turns, expected) || RESPONSE_CHANNEL_TYPE.RESTFUL;

    return (
      <div className="bg-layout" id="test_case_list__layout">
        <NiSpin spinning={loading}>
          <div className="page-body">
            <div
              ref={(bodyContent: HTMLDivElement) => {
                this.$bodyContent = bodyContent;
              }}
              className="body-content"
            >
              <NiTable
                dataSource={data}
                rowKey={(record: TestCaseRun) => {
                  return record.testCase && record.testCase.id || 'new';
                }}
                columns={this.getColumns()}
                onRow={(record: TestCaseRun, index?: number) => ({
                  onClick: () => {
                    this.setState({ testRecord: record });
                  }
                })}
              />
              <Drawer
                closable={false}
                onClose={() => {
                  this.setState({ testRecord: undefined });
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {`${run && run.testCase && run.testCase.name}(${USER_CHANNEL_NAME_MAP[channel]})`}
                    <a
                      key="debug"
                      style={{ color: contentFontColor }}
                      href={`${window.location.origin}/view-log?org_id=${getOrgId()}&agent_id=${getAgentId()}&user_id=${getUserId()}`} target="_blank">
                      <BugOutlined
                      />
                    </a>
                  </div>
                }
                width={500}
                open={!!testRecord}
                footer={
                  EditPermission() &&
                  <div style={{ display: 'flex', justifyContent: 'center', flex: 0.8 }}>
                    {
                      running
                        ? <Progress percent={percent} size="small" />
                        : <NiButton
                          type="primary"
                          onClick={() => {
                            this.handleRunTestCase(run && run.testCase);
                          }}
                        >
                          RUN
                        </NiButton>
                    }
                  </div>
                }
              >
                <TryItNowSession
                  turns={turns || []}
                  expectedTurns={expected}
                  channel={channel}
                />
              </Drawer>
            </div>
          </div>
        </NiSpin>
      </div >
    );
  }

  getDifferentChannel = (turns: TestCaseTurn[], expected?: TestCaseTurn[]) => {
    return RESPONSE_CHANNEL_TYPE.RESTFUL;
  }

  onInit = async () => {
    RefreshVersion(this);
    this.setState({ loading: true });

    const res: any = await API.testCase.testCaseRuns()
      .catch((err: Error) => {
        this.setState({ loading: false });
        message.error(err.message);
        throw err;
      });

    this.setState({ loading: false, data: res.map(testCaseRunToLocal) });

  };

  getColumns = () => {
    return [
      {
        title: localizedString('test_case_name'),
        dataIndex: ['testCase', 'name']
      },
      {
        title: localizedString('test_case_latest_time'),
        dataIndex: 'latestRunTime',
        width: 180,
        render: renderTime
      },
      {
        title: localizedString('test_case_run_result'),
        dataIndex: 'result',
        width: 80,
        render: (result: string) => {
          return <span
            style={{ color: result === 'Passed' ? 'green' : 'red' }}
          >
            {result}
          </span>;
        }
      },
      {
        title: '',
        className: 'hide',
        dataIndex: 'admin',
        width: 50,
        render: (admin: any, record: TestCaseRun, index: number) => {
          const disabled = isReadMode();

          return (
            <div className="icon-area">
              {
                !disabled &&
                (
                  <DeleteOutlined
                    className="delete-icon"
                    onClick={async (e: React.MouseEvent<HTMLElement>) => {
                      e.stopPropagation();
                      this.handleDeleteTestCase(record.testCase.id);
                    }}
                  />
                )}
            </div>
          );
        }
      }
    ];
  }

  handleRunTestCase = async (testCase?: TestCase) => {
    const { testRecord } = this.state;
    if (!testCase) {
      return;
    }
    this.setState({ running: true, percent: 0 });

    const newTurns: TestCaseTurn[] = [];
    let pass = true;

    for (const [i, turn] of testCase.turns.entries()) {
      const res: any = await API.common
        .getQuery(turn.query, turn.structureInput, i === 0, 'test_case')
        .catch((e: any) => {
          this.setState({ running: false });
          niConfirm({
            width: '80%',
            title: localizedString('common_error_info'),
            content: <pre style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>{e.message}</pre>
          });
          throw e;
        });

      newTurns.push({
        query: turn.query,
        structureInput: turn.structureInput,
        reply: JSON.stringify(res.response),
        stateInfo: JSON.stringify(res.state)
      });
      const stateExpected = turn.stateInfo && JSON.parse(turn.stateInfo);
      // if (!isEqual(JSON.stringify(res.response), turn.reply)) { // session还不能确保正确判断，暂时仍然使用response作为判断依据
      if (!isEqual(res.state.session, stateExpected && stateExpected.session)) {
        pass = false;
        message.warning(localizedString('test_case_run_failed'));
        break;
      }
      this.setState({ percent: 100 * ((i + 1) / testCase.turns.length) });
    }

    await API.testCase.updateRun(testCase.id, newTurns.map(testCaseTurnToServer))
      .catch((err: any) => {
        this.setState({ running: false });
        niConfirm({
          width: '80%',
          title: localizedString('common_error_info'),
          content: <pre style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>{err.data || err.message}</pre>
        });
        throw err;
      });

    this.setState({ running: false });
    if (testRecord && pass) {
      message.success(localizedString('test_case_run_passed'));
      const testedRecord = cloneDeep(testRecord);
      testedRecord.turns = newTurns;
      this.setState({ testRecord: testedRecord });
    }
    this.onInit();
  }

  handleDeleteTestCase = async (testCaseId: string) => {
    this.setState({ loading: false });
    await API.testCase.delete(testCaseId)
      .catch((err: Error) => {
        this.setState({ loading: false });
        message.error(err.message);
        throw err;
      });
    this.setState({ loading: false }, this.onInit);
    message.success(localizedString('common_delete_success'));
  }
}

export default (props: RouteComponentProps<{}>) =>
  <AgentContext.Consumer>
    {($agent: AgentContextProps) => (
      agentBaseComp($agent, <TestCaseComp {...props} />)
    )}
  </AgentContext.Consumer>;
